import { jsxs, jsx } from "react/jsx-runtime";
import classNames from "classnames";
import { memo, useState, useContext, useRef, useEffect, useCallback, useMemo } from "react";
import { DropdownContentContainerProvider } from "../Dropdown.js";
import { useElementTopOffset } from "./useElementTopOffset.js";
import { PreventCloseContext } from "../PreventCloseContext.js";
import { ThemeSchemeContext, TitleThemeSchemeContext } from "./ThemeSchemeContext.js";
import { NavigationContext } from "../../Navigation.js";
import { useClassNamePrefix } from "../../auxiliary/useClassNamePrefix.js";
import { toViewClass } from "../../utils/toViewClass.js";
import { toThemeClass } from "../../utils/toThemeClass.js";
import { toSchemeClass } from "../../utils/toSchemeClass.js";
import { Button } from "../Forms/Button/Button.js";
import { Icon } from "../Icon/Icon.js";
import { toStateClass } from "../../utils/toStateClass.js";
import { Stack } from "../Stack/Stack.js";
const PREVENT_HAPPENED_RECENTLY = 100;
const LayoutChrome = memo(({
  children,
  navigation,
  pageScheme,
  pageTheme,
  pageThemeContent,
  pageThemeControls,
  scheme = "light",
  sidebarFooter,
  sidebarHeader,
  switchers,
  theme,
  themeContent,
  themeControls,
  titleScheme,
  titleTheme,
  titleThemeContent,
  titleThemeControls
}) => {
  const prefix = useClassNamePrefix();
  const [collapsed, setCollapsed] = useState(true);
  const [isScrolled, setIsScrolled] = useState(false);
  const navigationContext = useContext(NavigationContext);
  const preventedAt = useRef(null);
  useEffect(() => {
    const wasPrevented = preventedAt.current ? new Date().valueOf() - preventedAt.current.valueOf() <= PREVENT_HAPPENED_RECENTLY : false;
    if (!wasPrevented) {
      setCollapsed(true);
    }
  }, [navigationContext]);
  const preventMenuClose = useCallback(() => {
    preventedAt.current = new Date();
  }, []);
  const toggleCollapsed = useCallback(() => {
    setCollapsed(!collapsed);
  }, [collapsed, setCollapsed]);
  const contentRef = useRef(null);
  const barContentOffsetTop = useElementTopOffset(contentRef);
  useEffect(() => {
    const contentRefCopy = contentRef.current;
    const listener = () => {
      const scrollTop = Math.floor(contentRefCopy?.scrollTop ?? 0);
      const nextIsScrolled = scrollTop > 1;
      if (isScrolled !== nextIsScrolled) {
        setIsScrolled(nextIsScrolled);
      }
    };
    contentRefCopy?.addEventListener("scroll", listener);
    return () => {
      contentRefCopy?.removeEventListener("scroll", listener);
    };
  }, [contentRef, isScrolled]);
  const themeScheme = useMemo(() => ({
    scheme: pageScheme ?? scheme,
    theme: pageTheme ?? theme,
    themeContent: pageThemeContent ?? themeContent,
    themeControls: pageThemeControls ?? themeControls
  }), [
    pageScheme,
    pageTheme,
    pageThemeContent,
    pageThemeControls,
    scheme,
    theme,
    themeContent,
    themeControls
  ]);
  const titleThemeScheme = useMemo(() => ({
    scheme: titleScheme ?? pageScheme ?? scheme,
    theme: titleTheme ?? pageTheme ?? theme,
    themeContent: titleThemeContent ?? pageThemeContent ?? themeContent,
    themeControls: titleThemeControls ?? pageThemeControls ?? themeControls
  }), [
    pageScheme,
    pageTheme,
    pageThemeContent,
    pageThemeControls,
    scheme,
    theme,
    themeContent,
    themeControls,
    titleScheme,
    titleTheme,
    titleThemeContent,
    titleThemeControls
  ]);
  const layoutRef = useRef(null);
  useEffect(() => {
    if (!layoutRef.current) {
      return;
    }
    const layout = layoutRef.current;
    const keyboardListener = (event) => {
      if (event.code === "Escape" && !collapsed) {
        document.getElementById("cui-menu-button")?.focus();
        setCollapsed(true);
      }
    };
    layout.addEventListener("keyup", keyboardListener);
    return () => {
      layout.removeEventListener("keyup", keyboardListener);
    };
  });
  const barClassName = `${prefix}layout-chrome-bar`;
  const hasBar = !!(sidebarHeader || switchers || navigation || sidebarFooter);
  return /* @__PURE__ */ jsxs(
    "div",
    {
      ref: layoutRef,
      className: classNames(
        `${prefix}layout-chrome`,
        toViewClass("no-bar", !hasBar),
        toThemeClass(themeContent ?? theme, themeControls ?? theme),
        toSchemeClass(scheme),
        toViewClass("collapsed", collapsed)
      ),
      style: useMemo(() => barContentOffsetTop ? { "--cui-bar-content-offset-top": `${barContentOffsetTop}px` } : void 0, [barContentOffsetTop]),
      children: [
        hasBar && /* @__PURE__ */ jsx(DropdownContentContainerProvider, { children: /* @__PURE__ */ jsx(PreventCloseContext.Provider, { value: preventMenuClose, children: /* @__PURE__ */ jsxs("div", { className: barClassName, children: [
          /* @__PURE__ */ jsxs("div", { className: `${barClassName}-header`, children: [
            sidebarHeader && /* @__PURE__ */ jsx("div", { className: `${barClassName}-header-inner`, children: sidebarHeader }),
            /* @__PURE__ */ jsxs(Button, { id: "cui-menu-button", distinction: "seamless", className: `${prefix}layout-chrome-navigation-button`, onClick: toggleCollapsed, children: [
              /* @__PURE__ */ jsx("span", { className: `${prefix}chrome-menu-button-label`, children: "Menu" }),
              /* @__PURE__ */ jsx(Icon, { blueprintIcon: collapsed ? "menu" : "cross" })
            ] })
          ] }),
          switchers && /* @__PURE__ */ jsx("div", { className: `${barClassName}-switchers`, children: switchers }),
          navigation && /* @__PURE__ */ jsxs("div", { ref: contentRef, className: `${barClassName}-body`, children: [
            /* @__PURE__ */ jsx("span", { className: classNames(
              `${barClassName}-body-scrolled-indicator`,
              toStateClass("scrolled", isScrolled)
            ) }),
            /* @__PURE__ */ jsx(Stack, { direction: "vertical", children: navigation })
          ] }),
          sidebarFooter && /* @__PURE__ */ jsx("div", { className: `${barClassName}-footer`, children: sidebarFooter })
        ] }) }) }),
        /* @__PURE__ */ jsx(DropdownContentContainerProvider, { children: /* @__PURE__ */ jsx("div", { className: classNames(
          `${prefix}layout-chrome-body`,
          toSchemeClass(pageScheme ?? scheme),
          toThemeClass(pageThemeContent ?? pageTheme, pageThemeControls ?? pageTheme)
        ), children: /* @__PURE__ */ jsx(ThemeSchemeContext.Provider, { value: themeScheme, children: /* @__PURE__ */ jsx(TitleThemeSchemeContext.Provider, { value: titleThemeScheme, children }) }) }) }),
        /* @__PURE__ */ jsx("div", { id: "portal-root" })
      ]
    }
  );
});
LayoutChrome.displayName = "LayoutChrome";
export {
  LayoutChrome
};
//# sourceMappingURL=LayoutChrome.js.map
